import {
  socketManager,
  CrapsState,
  CrapsBet,
  CrapsBetResult,
} from '../../socket/SocketManager';

/* ──────────────────────── constants ──────────────────────── */

const FELT_GREEN = 0x0a6b0a;
const DARK_GREEN = 0x054a05;
const WOOD_BROWN = 0x3a1a0a;
const GOLD = 0xffd700;
const PUCK_GREEN = 0x00cc00;
const PUCK_BLACK = 0x111111;

const ZONE_BORDER = 0xccaa00;
const PASS_LINE_BG = 0x084208;
const DONT_PASS_BG = 0x3a0a0a;
const COME_BG = 0x0a4a0a;
const DONT_COME_BG = 0x3a1a0a;
const FIELD_BG = 0x0a3a2a;
const PROP_BG = 0x1a2a1a;
const PLACE_BG = 0x0a3a0a;
const ODDS_BG = 0x043a04;

const CHIP_DENOMINATIONS = [10, 25, 50, 100, 500];

const CHIP_COLORS: Record<number, number> = {
  10: 0xcc0000,
  25: 0x00aa00,
  50: 0x0044cc,
  100: 0x222222,
  500: 0x7b2fbe,
};

/** Payout odds displayed under each Place number */
const PLACE_ODDS: Record<number, string> = {
  4: '9:5',
  5: '7:5',
  6: '7:6',
  8: '7:6',
  9: '7:5',
  10: '9:5',
};

/** Place bet display labels (SIX and NINE spelled out) */
const PLACE_LABELS: Record<number, string> = {
  4: '4',
  5: '5',
  6: 'SIX',
  8: '8',
  9: 'NINE',
  10: '10',
};

interface BetZone {
  key: string;
  label: string;
  type: string;
  number?: number;
  rect: { x: number; y: number; w: number; h: number };
  bg: number;
  subLabel?: string;
  isWide?: boolean; // true = horizontal zone (chip goes bottom-right), false = tall (top-right)
}

/* ──────────────────────── CrapsGame ──────────────────────── */

export class CrapsGame {
  private elements: Phaser.GameObjects.GameObject[] = [];
  private timers: Phaser.Time.TimerEvent[] = [];
  private tweens: Phaser.Tweens.Tween[] = [];

  private selectedChip = 100;
  private useDirty = false;
  private balanceClean = 0;
  private balanceDirty = 0;

  private currentState: CrapsState | null = null;
  private pendingBets: CrapsBet[] = [];
  private isRolling = false;

  // UI references
  private balanceText: Phaser.GameObjects.Text | null = null;
  private currentBetText: Phaser.GameObjects.Text | null = null;
  private phaseText: Phaser.GameObjects.Text | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;
  private rollBtn: Phaser.GameObjects.Container | null = null;
  private toggleText: Phaser.GameObjects.Text | null = null;
  private puckGraphics: Phaser.GameObjects.Graphics | null = null;
  private puckText: Phaser.GameObjects.Text | null = null;
  private totalText: Phaser.GameObjects.Text | null = null;
  private diceContainers: Phaser.GameObjects.Container[] = [];
  private chipSelectors: Phaser.GameObjects.Container[] = [];
  private chipContainers: Phaser.GameObjects.Container[] = [];

  private betZones: BetZone[] = [];
  private zoneGraphics: Phaser.GameObjects.Graphics | null = null;

  constructor(
    private scene: Phaser.Scene,
    private graphics: Phaser.GameObjects.Graphics,
    private building: any,
    private stateData: any,
    private onBack: () => void,
  ) {
    try {
      const gameScene = this.scene.scene.get('GameScene') as any;
      if (gameScene) {
        this.balanceDirty = gameScene.moneyDirty ?? 500;
        this.balanceClean = gameScene.moneyClean ?? 0;
      }
    } catch {
      // fallback
    }

    // Load existing state
    const existing: CrapsState | undefined = this.stateData?.crapsState;
    if (existing) {
      this.currentState = existing;
    }
  }

  /* =================================================================
   *  RENDER
   * ================================================================= */

  render(): void {
    this.drawTableBackground();
    this.buildBetZones();
    this.drawBetZones();
    this.renderPhaseDisplay();
    this.renderPuck();
    this.renderControlsBar();
    this.renderBetChips();
  }

  /* =================================================================
   *  TABLE BACKGROUND — 3-column felt with wood border
   * ================================================================= */

  private drawTableBackground(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;

    // Full-screen dark background
    this.graphics.fillStyle(0x0a0a0a, 1);
    this.graphics.fillRect(0, 0, W, H);

    // Table area: from top to the controls bar
    const tableBottom = H * 0.88;

    // Outer wood border
    this.graphics.fillStyle(WOOD_BROWN, 1);
    this.graphics.fillRoundedRect(
      W * 0.01,
      H * 0.02,
      W * 0.98,
      tableBottom - H * 0.02,
      W * 0.012,
    );

    // Inner felt
    const feltPad = W * 0.015;
    this.graphics.fillStyle(FELT_GREEN, 1);
    this.graphics.fillRoundedRect(
      W * 0.01 + feltPad,
      H * 0.02 + feltPad,
      W * 0.98 - feltPad * 2,
      tableBottom - H * 0.02 - feltPad * 2,
      W * 0.008,
    );

    // Gold border line
    this.graphics.lineStyle(Math.max(2, W * 0.003), GOLD, 0.7);
    this.graphics.strokeRoundedRect(
      W * 0.01 + feltPad,
      H * 0.02 + feltPad,
      W * 0.98 - feltPad * 2,
      tableBottom - H * 0.02 - feltPad * 2,
      W * 0.008,
    );

    // Right column wood panel (80%-100% of table inner area)
    const rightX = W * 0.80;
    const rightW = W * 0.98 - feltPad - rightX;
    this.graphics.fillStyle(WOOD_BROWN, 0.4);
    this.graphics.fillRect(rightX, H * 0.02 + feltPad, rightW, tableBottom - H * 0.02 - feltPad * 2);
    this.graphics.lineStyle(1, GOLD, 0.3);
    this.graphics.strokeRect(rightX, H * 0.02 + feltPad, rightW, tableBottom - H * 0.02 - feltPad * 2);

    // Controls bar background
    this.graphics.fillStyle(0x1a1a1a, 1);
    this.graphics.fillRect(0, tableBottom, W, H - tableBottom);
    this.graphics.lineStyle(2, GOLD, 0.4);
    this.graphics.beginPath();
    this.graphics.moveTo(0, tableBottom);
    this.graphics.lineTo(W, tableBottom);
    this.graphics.strokePath();
  }

  /* =================================================================
   *  BET ZONES — Real craps table layout (3 columns)
   * ================================================================= */

  private buildBetZones(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    this.betZones = [];

    // ── LEFT COLUMN: Propositions (0-25% W) ──
    const leftX = W * 0.03;
    const leftW = W * 0.22;
    const propTop = H * 0.06;
    const propBottom = H * 0.85;
    const propTotalH = propBottom - propTop;

    // 6 rows of proposition bets
    const propRows = [
      { key: 'ANY_7', label: 'SEVEN', type: 'ANY_7', subLabel: '4 to 1', split: false },
      { key: 'ANY_CRAPS', label: 'ANY CRAPS', type: 'ANY_CRAPS', subLabel: '7 to 1', split: false },
      { key: 'HARD_4_10', label: '', type: '', subLabel: '', split: true,
        left: { key: 'HARD_4', label: 'HARD 4', type: 'HARD_4', subLabel: '8 to 1' },
        right: { key: 'HARD_10', label: 'HARD 10', type: 'HARD_10', subLabel: '8 to 1' },
      },
      { key: 'HARD_6_8', label: '', type: '', subLabel: '', split: true,
        left: { key: 'HARD_6', label: 'HARD 6', type: 'HARD_6', subLabel: '10 to 1' },
        right: { key: 'HARD_8', label: 'HARD 8', type: 'HARD_8', subLabel: '10 to 1' },
      },
      { key: 'ACES_BOXCARS', label: '', type: '', subLabel: '', split: true,
        left: { key: 'ACES', label: 'ACES', type: 'ACES', subLabel: '31 to 1' },
        right: { key: 'BOXCARS', label: 'BOXCARS', type: 'BOXCARS', subLabel: '31 to 1' },
      },
      { key: 'YO', label: 'YO', type: 'YO', subLabel: '16 to 1', split: false },
    ];

    const propRowH = propTotalH / propRows.length;
    const propGap = H * 0.004;

    for (let i = 0; i < propRows.length; i++) {
      const row = propRows[i];
      const ry = propTop + i * propRowH + propGap / 2;
      const rh = propRowH - propGap;

      if ((row as any).split) {
        const splitRow = row as any;
        const halfW = (leftW - propGap) / 2;
        this.betZones.push({
          key: splitRow.left.key,
          label: splitRow.left.label,
          type: splitRow.left.type,
          rect: { x: leftX, y: ry, w: halfW, h: rh },
          bg: PROP_BG,
          subLabel: splitRow.left.subLabel,
          isWide: false,
        });
        this.betZones.push({
          key: splitRow.right.key,
          label: splitRow.right.label,
          type: splitRow.right.type,
          rect: { x: leftX + halfW + propGap, y: ry, w: halfW, h: rh },
          bg: PROP_BG,
          subLabel: splitRow.right.subLabel,
          isWide: false,
        });
      } else {
        this.betZones.push({
          key: row.key,
          label: row.label,
          type: row.type,
          rect: { x: leftX, y: ry, w: leftW, h: rh },
          bg: PROP_BG,
          subLabel: row.subLabel,
          isWide: false,
        });
      }
    }

    // ── CENTER COLUMN: Main table (25-80% W) ──
    const centerX = W * 0.26;
    const centerW = W * 0.53;

    // PASS LINE: H*0.75 to H*0.87
    this.betZones.push({
      key: 'PASS_LINE',
      label: 'P A S S   L I N E',
      type: 'PASS_LINE',
      rect: { x: centerX, y: H * 0.75, w: centerW, h: H * 0.12 },
      bg: PASS_LINE_BG,
      isWide: true,
    });

    // PASS ODDS: thin strip above pass line
    this.betZones.push({
      key: 'PASS_ODDS',
      label: 'ODDS',
      type: 'PASS_ODDS',
      rect: { x: centerX, y: H * 0.72, w: centerW, h: H * 0.03 },
      bg: ODDS_BG,
      isWide: true,
    });

    // DON'T PASS BAR: H*0.64 to H*0.72
    this.betZones.push({
      key: 'DONT_PASS',
      label: "Don't Pass Bar",
      type: 'DONT_PASS',
      rect: { x: centerX, y: H * 0.64, w: centerW, h: H * 0.08 },
      bg: DONT_PASS_BG,
      isWide: true,
    });

    // DON'T PASS ODDS: thin strip above don't pass
    this.betZones.push({
      key: 'DONT_PASS_ODDS',
      label: 'ODDS',
      type: 'DONT_PASS_ODDS',
      rect: { x: centerX, y: H * 0.61, w: centerW, h: H * 0.03 },
      bg: ODDS_BG,
      isWide: true,
    });

    // FIELD: H*0.49 to H*0.61
    this.betZones.push({
      key: 'FIELD',
      label: 'FIELD',
      type: 'FIELD',
      rect: { x: centerX, y: H * 0.49, w: centerW, h: H * 0.12 },
      bg: FIELD_BG,
      subLabel: 'DOUBLE (2) | 3 . 4 . 9 . 10 . 11 | TRIPLE (12)',
      isWide: true,
    });

    // COME: H*0.35 to H*0.49
    this.betZones.push({
      key: 'COME',
      label: 'C O M E',
      type: 'COME',
      rect: { x: centerX, y: H * 0.35, w: centerW, h: H * 0.14 },
      bg: COME_BG,
      isWide: true,
    });

    // DON'T COME BAR: H*0.28 to H*0.35
    this.betZones.push({
      key: 'DONT_COME',
      label: "Don't Come Bar",
      type: 'DONT_COME',
      rect: { x: centerX, y: H * 0.28, w: centerW, h: H * 0.07 },
      bg: DONT_COME_BG,
      isWide: true,
    });

    // COME ODDS: thin strip above Don't Come
    this.betZones.push({
      key: 'COME_ODDS',
      label: 'COME ODDS',
      type: 'COME_ODDS',
      rect: { x: centerX, y: H * 0.25, w: centerW, h: H * 0.03 },
      bg: ODDS_BG,
      isWide: true,
    });

    // DONT COME ODDS
    this.betZones.push({
      key: 'DONT_COME_ODDS',
      label: "DC ODDS",
      type: 'DONT_COME_ODDS',
      rect: { x: centerX, y: H * 0.22, w: centerW, h: H * 0.03 },
      bg: ODDS_BG,
      isWide: true,
    });

    // PLACE BETS: H*0.06 to H*0.22 — 6 boxes
    const placeNums = [4, 5, 6, 8, 9, 10];
    const placeW = centerW / placeNums.length;
    const placeY = H * 0.06;
    const placeH = H * 0.16;

    placeNums.forEach((num, i) => {
      const zoneW = i === placeNums.length - 1 ? centerW - i * placeW : placeW;
      this.betZones.push({
        key: `PLACE_${num}`,
        label: PLACE_LABELS[num],
        type: 'PLACE_WIN',
        number: num,
        rect: { x: centerX + i * placeW, y: placeY, w: zoneW, h: placeH },
        bg: PLACE_BG,
        subLabel: PLACE_ODDS[num],
        isWide: false,
      });
    });

    // ── RIGHT COLUMN: Side decorative zones (80-100% W) ──
    const rightX = W * 0.80;
    const rightW = W * 0.17;

    // Right column Don't Come continuation
    this.betZones.push({
      key: 'RIGHT_DONT_COME',
      label: "Don't Come",
      type: 'DONT_COME',
      rect: { x: rightX, y: H * 0.06, w: rightW, h: H * 0.25 },
      bg: DONT_COME_BG,
      isWide: false,
    });

    // Right column Don't Pass continuation
    this.betZones.push({
      key: 'RIGHT_DONT_PASS',
      label: "Don't Pass",
      type: 'DONT_PASS',
      rect: { x: rightX, y: H * 0.32, w: rightW, h: H * 0.25 },
      bg: DONT_PASS_BG,
      isWide: false,
    });
  }

  private drawBetZones(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;

    this.zoneGraphics = this.scene.add.graphics();
    this.zoneGraphics.setDepth(100);
    this.elements.push(this.zoneGraphics);

    for (const zone of this.betZones) {
      const { x, y, w, h } = zone.rect;
      const cornerR = Math.min(w, h) * 0.08;

      // Zone background
      this.zoneGraphics.fillStyle(zone.bg, 0.88);
      this.zoneGraphics.fillRoundedRect(x, y, w, h, cornerR);
      this.zoneGraphics.lineStyle(Math.max(1, W * 0.0015), ZONE_BORDER, 0.65);
      this.zoneGraphics.strokeRoundedRect(x, y, w, h, cornerR);

      // Main label
      const fontSize = this.calcFontSize(w, h, zone.label);
      const labelY = zone.subLabel ? y + h * 0.38 : y + h * 0.5;
      const label = this.scene.add
        .text(x + w / 2, labelY, zone.label, {
          fontSize: fontSize + 'px',
          fontFamily: '"Arial Black", "Helvetica Neue", Arial, sans-serif',
          color: '#e6d88a',
          fontStyle: 'bold',
          align: 'center',
          wordWrap: { width: w - w * 0.1 },
        })
        .setOrigin(0.5)
        .setDepth(101);
      this.elements.push(label);

      // Sub-label (odds / annotations)
      if (zone.subLabel && zone.subLabel.length > 0) {
        const subFontSize = Math.max(Math.round(fontSize * 0.65), 7);
        const sub = this.scene.add
          .text(x + w / 2, y + h * 0.68, zone.subLabel, {
            fontSize: subFontSize + 'px',
            fontFamily: 'monospace',
            color: '#aaa888',
            align: 'center',
            wordWrap: { width: w - w * 0.06 },
          })
          .setOrigin(0.5)
          .setDepth(101);
        this.elements.push(sub);
      }

      // Draw dice face icons for hardway zones
      this.drawZoneDiceIcons(zone, x, y, w, h);

      // Clickable zone
      const hitZone = this.scene.add
        .zone(x + w / 2, y + h / 2, w, h)
        .setInteractive({ useHandCursor: true })
        .setDepth(102);

      hitZone.on('pointerdown', () => this.onZoneClick(zone));

      // Hover highlight
      const hoverOverlay = this.scene.add.graphics();
      hoverOverlay.setDepth(103);
      hoverOverlay.setAlpha(0);
      this.elements.push(hoverOverlay);

      hitZone.on('pointerover', () => {
        hoverOverlay.clear();
        hoverOverlay.lineStyle(Math.max(2, W * 0.002), 0xffffff, 0.9);
        hoverOverlay.strokeRoundedRect(x + 1, y + 1, w - 2, h - 2, cornerR);
        hoverOverlay.setAlpha(1);
      });
      hitZone.on('pointerout', () => {
        hoverOverlay.setAlpha(0);
      });

      this.elements.push(hitZone);
    }

    // Draw the FIELD special decorations (circled 2 and circled 12)
    this.drawFieldDecorations();
  }

  /** Draw small dice face icons on hardway and proposition zones */
  private drawZoneDiceIcons(zone: BetZone, x: number, y: number, w: number, h: number): void {
    const W = this.scene.scale.width;
    const diceSize = Math.max(W * 0.012, 8);
    const pipR = Math.max(diceSize * 0.12, 1.2);
    const gap = diceSize * 0.3;

    let dice1 = 0;
    let dice2 = 0;

    switch (zone.key) {
      case 'HARD_4':  dice1 = 2; dice2 = 2; break;
      case 'HARD_6':  dice1 = 3; dice2 = 3; break;
      case 'HARD_8':  dice1 = 4; dice2 = 4; break;
      case 'HARD_10': dice1 = 5; dice2 = 5; break;
      case 'ACES':    dice1 = 1; dice2 = 1; break;
      case 'BOXCARS': dice1 = 6; dice2 = 6; break;
      case 'YO':      dice1 = 5; dice2 = 6; break;
      default: return;
    }

    const g = this.scene.add.graphics();
    g.setDepth(101);

    // Position dice at bottom of zone
    const diceY = y + h * 0.82;
    const diceX1 = x + w / 2 - diceSize - gap / 2;
    const diceX2 = x + w / 2 + gap / 2;

    // Draw both dice
    for (const [dx, val] of [[diceX1, dice1], [diceX2, dice2]] as [number, number][]) {
      g.fillStyle(0xffffff, 0.9);
      g.fillRoundedRect(dx, diceY, diceSize, diceSize, diceSize * 0.15);
      g.lineStyle(1, 0x888888, 0.7);
      g.strokeRoundedRect(dx, diceY, diceSize, diceSize, diceSize * 0.15);

      // Pips
      g.fillStyle(0x222222, 1);
      const positions = this.getMiniPipPositions(val, dx, diceY, diceSize);
      for (const [px, py] of positions) {
        g.fillCircle(px, py, pipR);
      }
    }

    this.elements.push(g);
  }

  /** Get pip positions for miniature dice */
  private getMiniPipPositions(
    value: number, ox: number, oy: number, size: number,
  ): [number, number][] {
    const c = size / 2;
    const lo = size * 0.26;
    const hi = size * 0.74;

    const offset = (v: number): [number, number] => [ox + v, oy + v];
    const p = (px: number, py: number): [number, number] => [ox + px, oy + py];

    switch (value) {
      case 1: return [p(c, c)];
      case 2: return [p(hi, lo), p(lo, hi)];
      case 3: return [p(hi, lo), p(c, c), p(lo, hi)];
      case 4: return [p(lo, lo), p(hi, lo), p(lo, hi), p(hi, hi)];
      case 5: return [p(lo, lo), p(hi, lo), p(c, c), p(lo, hi), p(hi, hi)];
      case 6: return [p(lo, lo), p(lo, c), p(lo, hi), p(hi, lo), p(hi, c), p(hi, hi)];
      default: return [p(c, c)];
    }
  }

  /** Draw special FIELD decorations: circled 2 and circled 12 */
  private drawFieldDecorations(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const fieldZone = this.betZones.find((z) => z.key === 'FIELD');
    if (!fieldZone) return;

    const { x, y, w, h } = fieldZone.rect;
    const circleR = Math.max(W * 0.012, 8);
    const g = this.scene.add.graphics();
    g.setDepth(101);

    // "DOUBLE" + circled 2 on left
    const leftCircleX = x + w * 0.08;
    const circleY = y + h * 0.78;
    g.lineStyle(Math.max(1.5, W * 0.002), 0xff4444, 0.9);
    g.strokeCircle(leftCircleX, circleY, circleR);
    this.elements.push(g);

    const dblTxt = this.scene.add
      .text(leftCircleX, circleY - circleR - H * 0.015, 'DOUBLE', {
        fontSize: Math.max(Math.round(W * 0.009), 7) + 'px',
        fontFamily: 'monospace',
        color: '#ff4444',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(101);
    this.elements.push(dblTxt);

    const twoTxt = this.scene.add
      .text(leftCircleX, circleY, '2', {
        fontSize: Math.max(Math.round(W * 0.011), 8) + 'px',
        fontFamily: 'monospace',
        color: '#ff4444',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(101);
    this.elements.push(twoTxt);

    // "TRIPLE" + circled 12 on right
    const rightCircleX = x + w * 0.92;
    const g2 = this.scene.add.graphics();
    g2.setDepth(101);
    g2.lineStyle(Math.max(1.5, W * 0.002), 0xff4444, 0.9);
    g2.strokeCircle(rightCircleX, circleY, circleR);
    this.elements.push(g2);

    const trpTxt = this.scene.add
      .text(rightCircleX, circleY - circleR - H * 0.015, 'TRIPLE', {
        fontSize: Math.max(Math.round(W * 0.009), 7) + 'px',
        fontFamily: 'monospace',
        color: '#ff4444',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(101);
    this.elements.push(trpTxt);

    const twelveTxt = this.scene.add
      .text(rightCircleX, circleY, '12', {
        fontSize: Math.max(Math.round(W * 0.011), 8) + 'px',
        fontFamily: 'monospace',
        color: '#ff4444',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(101);
    this.elements.push(twelveTxt);
  }

  /** Calculate responsive font size based on zone dimensions */
  private calcFontSize(w: number, h: number, label: string): number {
    const W = this.scene.scale.width;
    const baseSize = Math.min(w * 0.14, h * 0.28, W * 0.018);
    // Shrink for long labels
    if (label.length > 12) return Math.max(Math.round(baseSize * 0.75), 7);
    if (label.length > 8) return Math.max(Math.round(baseSize * 0.85), 8);
    return Math.max(Math.round(baseSize), 8);
  }

  /* =================================================================
   *  BET ZONE CLICK — place chip in corner
   * ================================================================= */

  private onZoneClick(zone: BetZone): void {
    if (this.isRolling) return;

    console.log(
      `[CRAPS] Zone clicked: type=${zone.type}, number=${zone.number ?? 'none'}, chip=${this.selectedChip}, useDirty=${this.useDirty}`,
    );

    const bet: CrapsBet = {
      type: zone.type,
      amount: this.selectedChip,
      useDirty: this.useDirty,
    };
    if (zone.number !== undefined) {
      bet.number = zone.number;
    }

    this.pendingBets.push(bet);
    this.renderBetChips();
    this.updateCurrentBetText();

    // Flash the zone to confirm
    this.flashZone(zone, 0xffffff, 0.3);
  }

  private flashZone(zone: BetZone, color: number, alpha: number): void {
    const { x, y, w, h } = zone.rect;
    const cornerR = Math.min(w, h) * 0.08;
    const flash = this.scene.add.graphics();
    flash.fillStyle(color, alpha);
    flash.fillRoundedRect(x, y, w, h, cornerR);
    flash.setDepth(103);
    this.elements.push(flash);

    const tw = this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 300,
      onComplete: () => flash.destroy(),
    });
    this.tweens.push(tw);
  }

  /* =================================================================
   *  CHIP RENDERING — corner placement with badge
   * ================================================================= */

  /** Draw a chip at position with amount badge */
  private drawBetChip(
    x: number,
    y: number,
    totalAmount: number,
    highestDenom: number,
  ): Phaser.GameObjects.Container {
    const W = this.scene.scale.width;
    const chipR = Math.max(W * 0.011, 8);
    const color = this.getChipColorForAmount(highestDenom);
    const container = this.scene.add.container(x, y);

    // Chip body
    const g = this.scene.add.graphics();
    g.fillStyle(color, 1);
    g.fillCircle(0, 0, chipR);
    g.lineStyle(Math.max(1.5, W * 0.002), 0xffffff, 0.9);
    g.strokeCircle(0, 0, chipR);
    // Inner ring
    g.lineStyle(Math.max(0.8, W * 0.001), 0xffffff, 0.35);
    g.strokeCircle(0, 0, chipR * 0.65);
    container.add(g);

    // Denomination text on chip
    const denomText = highestDenom >= 1000 ? `${Math.round(highestDenom / 1000)}K` : `${highestDenom}`;
    const chipLabel = this.scene.add
      .text(0, 0, denomText, {
        fontSize: Math.max(Math.round(chipR * 0.7), 6) + 'px',
        fontFamily: 'monospace',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    container.add(chipLabel);

    // Badge circle on top-right of chip showing total
    const badgeR = Math.max(chipR * 0.5, 5);
    const badgeX = chipR * 0.7;
    const badgeY = -chipR * 0.7;
    const badge = this.scene.add.graphics();
    badge.fillStyle(0x000000, 0.8);
    badge.fillCircle(badgeX, badgeY, badgeR);
    badge.lineStyle(1, GOLD, 0.9);
    badge.strokeCircle(badgeX, badgeY, badgeR);
    container.add(badge);

    const amtStr = totalAmount >= 1000 ? `$${(totalAmount / 1000).toFixed(1)}K` : `$${totalAmount}`;
    const badgeText = this.scene.add
      .text(badgeX, badgeY, amtStr, {
        fontSize: Math.max(Math.round(badgeR * 0.85), 5) + 'px',
        fontFamily: 'monospace',
        color: '#ffd700',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    container.add(badgeText);

    container.setDepth(108);
    this.elements.push(container);
    return container;
  }

  /** Get chip color matching highest denomination */
  private getChipColorForAmount(amount: number): number {
    // Find highest denomination that fits
    let bestColor = CHIP_COLORS[10];
    for (const denom of CHIP_DENOMINATIONS) {
      if (denom <= amount) {
        bestColor = CHIP_COLORS[denom];
      }
    }
    return bestColor;
  }

  /** Render chips on zones — corner placement, grouped by zone */
  private renderBetChips(): void {
    // Destroy old chip containers
    for (const c of this.chipContainers) {
      try {
        c.destroy();
      } catch {
        /* ok */
      }
    }
    this.chipContainers = [];

    // Gather all bets grouped by zone key
    const allBets = [
      ...(this.currentState?.activeBets || []).map((b) => ({
        ...b,
        source: 'active' as const,
      })),
      ...this.pendingBets.map((b) => ({ ...b, source: 'pending' as const })),
    ];

    // Group by zone key and sum amounts
    const grouped: Record<string, { totalAmount: number; highestDenom: number; hasPending: boolean }> = {};
    for (const bet of allBets) {
      let zoneKey = bet.type;
      if ((bet as any).number) {
        zoneKey = `PLACE_${(bet as any).number}`;
      }
      if (!grouped[zoneKey]) {
        grouped[zoneKey] = { totalAmount: 0, highestDenom: 0, hasPending: false };
      }
      grouped[zoneKey].totalAmount += bet.amount;
      if (bet.amount > grouped[zoneKey].highestDenom) {
        grouped[zoneKey].highestDenom = bet.amount;
      }
      if (bet.source === 'pending') {
        grouped[zoneKey].hasPending = true;
      }
    }

    // Draw one chip per zone in the corner
    for (const [zoneKey, info] of Object.entries(grouped)) {
      const zone = this.betZones.find((z) => z.key === zoneKey);
      if (!zone) continue;

      const { x, y, w, h } = zone.rect;
      const W = this.scene.scale.width;
      const chipR = Math.max(W * 0.011, 8);

      // Corner placement: bottom-right for wide zones, top-right for tall
      let chipX: number;
      let chipY: number;
      if (zone.isWide) {
        chipX = x + w - chipR - w * 0.03;
        chipY = y + h - chipR - h * 0.1;
      } else {
        chipX = x + w - chipR - w * 0.08;
        chipY = y + chipR + h * 0.08;
      }

      const chip = this.drawBetChip(chipX, chipY, info.totalAmount, info.highestDenom);

      // Pending bets get a pulsing glow
      if (info.hasPending) {
        chip.setAlpha(0.85);
        const tw = this.scene.tweens.add({
          targets: chip,
          alpha: 1,
          duration: 400,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.InOut',
        });
        this.tweens.push(tw);
      }

      this.chipContainers.push(chip);
    }
  }

  /* =================================================================
   *  CHIP SELECTOR — Bottom controls bar
   * ================================================================= */

  private renderControlsBar(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const barY = H * 0.88;
    const barH = H * 0.12;
    const barCenterY = barY + barH * 0.45;

    // ── RETOUR (back) button — top-left ──
    const backBtn = this.scene.add
      .text(W * 0.02, H * 0.01, 'RETOUR', {
        fontSize: Math.max(Math.round(W * 0.012), 10) + 'px',
        fontFamily: 'monospace',
        color: '#aaaaaa',
        backgroundColor: '#222222',
        padding: { x: Math.round(W * 0.008), y: Math.round(H * 0.005) },
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(115);
    backBtn.on('pointerdown', () => this.onBack());
    backBtn.on('pointerover', () => backBtn.setAlpha(0.8));
    backBtn.on('pointerout', () => backBtn.setAlpha(1));
    this.elements.push(backBtn);

    // ── Left: Dice display area ──
    // (dice are placed here during/after roll animation — just reserve space)

    // ── Center: Chip selector ──
    this.renderChipSelector(barCenterY);

    // ── Propre/Sale Toggle ──
    const toggleX = W * 0.02;
    this.toggleText = this.scene.add
      .text(toggleX, barCenterY + barH * 0.3, this.getToggleLabel(), {
        fontSize: Math.max(Math.round(W * 0.012), 10) + 'px',
        fontFamily: 'monospace',
        color: this.useDirty ? '#ff4444' : '#44ff44',
        backgroundColor: '#222222',
        padding: { x: Math.round(W * 0.008), y: Math.round(H * 0.004) },
      })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(100);

    this.toggleText.on('pointerdown', () => {
      if (this.isRolling) return;
      this.useDirty = !this.useDirty;
      this.toggleText!.setText(this.getToggleLabel());
      this.toggleText!.setColor(this.useDirty ? '#ff4444' : '#44ff44');
    });
    this.toggleText.on('pointerover', () => this.toggleText!.setAlpha(0.8));
    this.toggleText.on('pointerout', () => this.toggleText!.setAlpha(1));
    this.elements.push(this.toggleText);

    // ── EFFACER (clear bets) button ──
    const clearX = W * 0.15;
    const clearBtn = this.scene.add
      .text(clearX, barCenterY + barH * 0.3, 'EFFACER', {
        fontSize: Math.max(Math.round(W * 0.012), 10) + 'px',
        fontFamily: 'monospace',
        color: '#ff6666',
        backgroundColor: '#331111',
        padding: { x: Math.round(W * 0.008), y: Math.round(H * 0.004) },
      })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(100);

    clearBtn.on('pointerdown', () => {
      if (this.isRolling) return;
      this.pendingBets = [];
      this.renderBetChips();
      this.updateCurrentBetText();
    });
    clearBtn.on('pointerover', () => clearBtn.setAlpha(0.8));
    clearBtn.on('pointerout', () => clearBtn.setAlpha(1));
    this.elements.push(clearBtn);

    // ── LANCER (Roll) button — large, purple ──
    const lancerX = W * 0.82;
    this.rollBtn = this.scene.add.container(lancerX, barCenterY);
    this.rollBtn.setDepth(110);

    const btnW = W * 0.13;
    const btnH = barH * 0.45;
    const btnBg = this.scene.add.graphics();
    btnBg.fillStyle(0x7b2fbe, 1);
    btnBg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, btnH * 0.25);
    btnBg.lineStyle(Math.max(2, W * 0.002), 0xccaaff, 0.7);
    btnBg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, btnH * 0.25);
    this.rollBtn.add(btnBg);

    const btnText = this.scene.add
      .text(0, 0, 'LANCER', {
        fontSize: Math.max(Math.round(W * 0.016), 12) + 'px',
        fontFamily: '"Arial Black", Arial, sans-serif',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.rollBtn.add(btnText);

    const btnHit = this.scene.add
      .zone(0, 0, btnW, btnH)
      .setInteractive({ useHandCursor: true });
    this.rollBtn.add(btnHit);

    btnHit.on('pointerdown', () => this.roll());
    btnHit.on('pointerover', () => {
      if (!this.isRolling) this.rollBtn!.setScale(1.05);
    });
    btnHit.on('pointerout', () => this.rollBtn!.setScale(1.0));
    this.elements.push(this.rollBtn);

    // ── Right: Current bet + Balance display ──
    const infoX = W * 0.55;
    this.currentBetText = this.scene.add
      .text(infoX, barCenterY - barH * 0.15, '$0.00 CURRENT BET', {
        fontSize: Math.max(Math.round(W * 0.011), 9) + 'px',
        fontFamily: 'monospace',
        color: '#ffd700',
      })
      .setOrigin(0, 0.5)
      .setDepth(100);
    this.elements.push(this.currentBetText);
    this.updateCurrentBetText();

    this.balanceText = this.scene.add
      .text(infoX, barCenterY + barH * 0.15, this.getBalanceLabel(), {
        fontSize: Math.max(Math.round(W * 0.011), 9) + 'px',
        fontFamily: 'monospace',
        color: '#cccccc',
      })
      .setOrigin(0, 0.5)
      .setDepth(100);
    this.elements.push(this.balanceText);

    // ── Result text (centered) ──
    this.resultText = this.scene.add
      .text(W * 0.5, barY - H * 0.03, '', {
        fontSize: Math.max(Math.round(W * 0.022), 16) + 'px',
        fontFamily: '"Arial Black", Arial, sans-serif',
        color: '#e6b800',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(115)
      .setAlpha(0);
    this.elements.push(this.resultText);
  }

  private renderChipSelector(barCenterY: number): void {
    const W = this.scene.scale.width;
    const chipSpacing = Math.min(W * 0.055, 50);
    const totalChipsW = (CHIP_DENOMINATIONS.length - 1) * chipSpacing;
    const startX = W * 0.35 - totalChipsW / 2;
    const chipRadius = Math.min(W * 0.018, 16);

    // Label
    const label = this.scene.add
      .text(startX - W * 0.05, barCenterY, 'Mise:', {
        fontSize: Math.max(Math.round(W * 0.013), 10) + 'px',
        fontFamily: 'monospace',
        color: '#aaaaaa',
      })
      .setOrigin(0, 0.5)
      .setDepth(100);
    this.elements.push(label);

    CHIP_DENOMINATIONS.forEach((amount, i) => {
      const chipX = startX + i * chipSpacing;
      const container = this.scene.add.container(chipX, barCenterY);
      container.setDepth(109);

      const color = CHIP_COLORS[amount] || 0x888888;
      const isSelected = this.selectedChip === amount;

      const g = this.scene.add.graphics();

      // Glow ring for selected
      if (isSelected) {
        g.lineStyle(Math.max(3, W * 0.003), 0xffdd44, 0.9);
        g.strokeCircle(0, 0, chipRadius + Math.max(3, W * 0.003));
      }

      // Chip body
      g.fillStyle(color, 1);
      g.fillCircle(0, 0, chipRadius);
      g.lineStyle(Math.max(2, W * 0.002), 0xffffff, 0.9);
      g.strokeCircle(0, 0, chipRadius);
      g.lineStyle(Math.max(0.8, W * 0.001), 0xffffff, 0.35);
      g.strokeCircle(0, 0, chipRadius * 0.65);
      container.add(g);

      // Amount text
      const txt = this.scene.add
        .text(0, 0, `${amount}`, {
          fontSize: Math.max(Math.round(chipRadius * 0.65), 7) + 'px',
          fontFamily: 'monospace',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      container.add(txt);

      // Hit area
      const hitArea = this.scene.add
        .zone(0, 0, chipRadius * 2 + 6, chipRadius * 2 + 6)
        .setInteractive({ useHandCursor: true });
      container.add(hitArea);

      hitArea.on('pointerdown', () => {
        if (this.isRolling) return;
        this.selectedChip = amount;
        this.refreshChipSelector();
      });
      hitArea.on('pointerover', () => container.setScale(1.1));
      hitArea.on('pointerout', () => container.setScale(1.0));

      this.chipSelectors.push(container);
      this.elements.push(container);
    });
  }

  private refreshChipSelector(): void {
    const H = this.scene.scale.height;
    for (const c of this.chipSelectors) {
      try {
        c.destroy();
      } catch {
        /* ok */
      }
    }
    this.chipSelectors = [];
    const barCenterY = H * 0.88 + H * 0.12 * 0.45;
    this.renderChipSelector(barCenterY);
  }

  /* =================================================================
   *  PHASE DISPLAY & PUCK
   * ================================================================= */

  private renderPhaseDisplay(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;

    let phaseLabel = 'COME OUT ROLL';
    if (this.currentState?.phase === 'POINT' && this.currentState.point) {
      phaseLabel = `POINT: ${this.currentState.point}`;
    }

    this.phaseText = this.scene.add
      .text(W * 0.53, H * 0.02, phaseLabel, {
        fontSize: Math.max(Math.round(W * 0.016), 13) + 'px',
        fontFamily: '"Arial Black", Arial, sans-serif',
        color: '#e6b800',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: Math.max(2, Math.round(W * 0.002)),
      })
      .setOrigin(0.5, 0)
      .setDepth(112);
    this.elements.push(this.phaseText);
  }

  private renderPuck(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const puckR = Math.max(W * 0.014, 10);

    if (
      this.currentState &&
      this.currentState.phase === 'POINT' &&
      this.currentState.point
    ) {
      // ON puck — position on the point number in Place bets row
      const pointZone = this.betZones.find(
        (z) =>
          z.type === 'PLACE_WIN' && z.number === this.currentState!.point,
      );
      if (!pointZone) return;

      const { x, y, w, h } = pointZone.rect;
      const puckX = x + w / 2;
      const puckY = y + h * 0.2;

      this.puckGraphics = this.scene.add.graphics();
      this.puckGraphics.setDepth(107);
      this.puckGraphics.fillStyle(PUCK_GREEN, 0.95);
      this.puckGraphics.fillCircle(puckX, puckY, puckR);
      this.puckGraphics.lineStyle(Math.max(2, W * 0.002), 0xffffff, 1);
      this.puckGraphics.strokeCircle(puckX, puckY, puckR);
      this.elements.push(this.puckGraphics);

      this.puckText = this.scene.add
        .text(puckX, puckY, 'ON', {
          fontSize: Math.max(Math.round(puckR * 0.8), 7) + 'px',
          fontFamily: '"Arial Black", Arial, sans-serif',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(107);
      this.elements.push(this.puckText);
    } else {
      // OFF puck — in Don't Come area
      const dcZone = this.betZones.find((z) => z.key === 'DONT_COME');
      if (!dcZone) return;

      const { x, y, w, h } = dcZone.rect;
      const puckX = x + w * 0.9;
      const puckY = y + h * 0.5;

      this.puckGraphics = this.scene.add.graphics();
      this.puckGraphics.setDepth(107);
      this.puckGraphics.fillStyle(PUCK_BLACK, 0.95);
      this.puckGraphics.fillCircle(puckX, puckY, puckR);
      this.puckGraphics.lineStyle(Math.max(2, W * 0.002), 0x888888, 1);
      this.puckGraphics.strokeCircle(puckX, puckY, puckR);
      this.elements.push(this.puckGraphics);

      this.puckText = this.scene.add
        .text(puckX, puckY, 'OFF', {
          fontSize: Math.max(Math.round(puckR * 0.7), 6) + 'px',
          fontFamily: '"Arial Black", Arial, sans-serif',
          color: '#999999',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(107);
      this.elements.push(this.puckText);
    }
  }

  /* =================================================================
   *  ROLL — SEND ACTION & ANIMATE
   * ================================================================= */

  private roll(): void {
    if (this.isRolling) return;

    const phase = this.currentState?.phase || 'COME_OUT';
    const point = this.currentState?.point ?? 'none';
    console.log(
      `[CRAPS] LANCER clicked, phase=${phase}, point=${point}, pendingBets=${this.pendingBets.length}, activeBets=${this.currentState?.activeBets?.length ?? 0}`,
    );

    // Must have at least one bet (pending or active)
    const hasActiveBets =
      this.currentState?.activeBets &&
      this.currentState.activeBets.length > 0;
    if (this.pendingBets.length === 0 && !hasActiveBets) {
      this.showError('Placez au moins un pari!');
      return;
    }

    this.isRolling = true;
    if (this.rollBtn) {
      this.rollBtn.setAlpha(0.5);
    }

    // Clear previous result
    if (this.resultText) {
      this.resultText.setAlpha(0);
      this.resultText.setText('');
    }

    const doRoll = () => {
      socketManager.sendAction('CASINO_PLAY_CRAPS', {
        buildingId: this.building.id,
        action: 'ROLL',
      });

      socketManager.onceActionResult((result) => {
        this.handleRollResult(result);
      });
    };

    // If there are pending bets, send PLACE_BET first, then ROLL
    if (this.pendingBets.length > 0) {
      console.log(
        `[CRAPS] Sending PLACE_BET with ${this.pendingBets.length} bets`,
      );
      socketManager.sendAction('CASINO_PLAY_CRAPS', {
        buildingId: this.building.id,
        action: 'PLACE_BET',
        bets: this.pendingBets,
      });

      socketManager.onceActionResult((betResult) => {
        if (!betResult.success) {
          console.error(`[CRAPS] PLACE_BET failed: ${betResult.error}`);
          this.isRolling = false;
          if (this.rollBtn) this.rollBtn.setAlpha(1);
          this.showError(betResult.error || 'Erreur de mise');
          return;
        }
        console.log(`[CRAPS] PLACE_BET accepted, now rolling`);
        this.pendingBets = [];
        this.updateCurrentBetText();
        doRoll();
      });
    } else {
      // Only active bets (already placed), just roll
      doRoll();
    }
  }

  private handleRollResult(result: any): void {
    if (result.success && result.data) {
      const data = result.data as Record<string, any>;
      const crapsResult = (data.details || data) as CrapsState;

      if (!crapsResult || !crapsResult.dice) {
        console.error('[CRAPS] Invalid result from server:', data);
        this.isRolling = false;
        if (this.rollBtn) this.rollBtn.setAlpha(1);
        return;
      }

      const dice = crapsResult.dice;
      const payout = data.payout || 0;
      console.log(
        `[CRAPS] Dice result: [${dice[0]}, ${dice[1]}] = ${dice[0] + dice[1]}, phase=${crapsResult.phase}, point=${crapsResult.point ?? 'none'}, payout=${payout}`,
      );
      if (crapsResult.results && crapsResult.results.length > 0) {
        crapsResult.results.forEach((r: any, i: number) => {
          console.log(
            `[CRAPS]   bet[${i}]: type=${r.bet.type}, outcome=${r.outcome}, payout=${r.payout}`,
          );
        });
      }

      // Update balances
      if (data.newClean !== undefined) this.balanceClean = data.newClean;
      if (data.newDirty !== undefined) this.balanceDirty = data.newDirty;

      // Clear pending bets (they've been submitted)
      this.pendingBets = [];

      // Animate dice, THEN show results
      this.animateDiceRoll(dice[0], dice[1]).then(() => {
        // Update state after animation
        this.currentState = crapsResult;

        // Show results
        this.onRollComplete(crapsResult, payout);
      });
    } else {
      this.isRolling = false;
      if (this.rollBtn) this.rollBtn.setAlpha(1);
      this.showError(result.error || 'Erreur');
    }
  }

  private onRollComplete(state: CrapsState, payout: number): void {
    // Update balance
    this.balanceText?.setText(this.getBalanceLabel());
    this.updateCurrentBetText();

    // Update phase display
    if (this.phaseText) {
      if (state.phase === 'POINT' && state.point) {
        this.phaseText.setText(`POINT: ${state.point}`);
      } else {
        this.phaseText.setText('COME OUT ROLL');
      }
    }

    // Destroy old puck and redraw
    if (this.puckGraphics) {
      this.puckGraphics.destroy();
      this.puckGraphics = null;
    }
    if (this.puckText) {
      this.puckText.destroy();
      this.puckText = null;
    }
    this.renderPuck();

    // Show bet results (flash winning/losing zones)
    if (state.results && state.results.length > 0) {
      this.showBetResults(state.results);
    }

    // Show payout message
    if (payout > 0) {
      this.showWin(payout);
    } else if (state.results && state.results.some((r) => r.outcome === 'LOSE')) {
      this.showLoss();
    }

    // Update bet chips on zones
    this.renderBetChips();

    // Re-enable controls after a brief delay
    const delayTimer = this.scene.time.delayedCall(800, () => {
      this.isRolling = false;
      if (this.rollBtn) this.rollBtn.setAlpha(1);
    });
    this.timers.push(delayTimer as any);
  }

  /* =================================================================
   *  DICE — CREATION & DRAWING
   * ================================================================= */

  private createDie(
    x: number,
    y: number,
    value: number,
    size: number,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    container.setDepth(110);

    // Shadow
    const shadow = this.scene.add.graphics();
    shadow.fillStyle(0x000000, 0.35);
    shadow.fillRoundedRect(size * 0.08, size * 0.08, size, size, size * 0.16);
    container.add(shadow);

    // White face
    const face = this.scene.add.graphics();
    face.fillStyle(0xffffff, 1);
    face.fillRoundedRect(0, 0, size, size, size * 0.16);
    face.lineStyle(Math.max(2, size * 0.04), 0x888888, 1);
    face.strokeRoundedRect(0, 0, size, size, size * 0.16);
    container.add(face);

    // Pips
    this.drawPips(container, value, size);

    this.elements.push(container);
    return container;
  }

  private drawPips(
    container: Phaser.GameObjects.Container,
    value: number,
    size: number,
  ): void {
    // Remove old pip graphics (skip shadow at 0 and face at 1)
    while (container.list.length > 2) {
      const last = container.list[container.list.length - 1];
      container.remove(last, true);
    }

    const pips = this.scene.add.graphics();
    pips.fillStyle(0x222222, 1);
    const pipR = size * 0.1;

    const positions = this.getPipPositions(value, size);
    for (const [px, py] of positions) {
      pips.fillCircle(px, py, pipR);
    }

    container.add(pips);
  }

  private getPipPositions(value: number, size: number): [number, number][] {
    const c = size / 2;
    const lo = size * 0.26;
    const hi = size * 0.74;

    switch (value) {
      case 1:
        return [[c, c]];
      case 2:
        return [
          [hi, lo],
          [lo, hi],
        ];
      case 3:
        return [
          [hi, lo],
          [c, c],
          [lo, hi],
        ];
      case 4:
        return [
          [lo, lo],
          [hi, lo],
          [lo, hi],
          [hi, hi],
        ];
      case 5:
        return [
          [lo, lo],
          [hi, lo],
          [c, c],
          [lo, hi],
          [hi, hi],
        ];
      case 6:
        return [
          [lo, lo],
          [lo, c],
          [lo, hi],
          [hi, lo],
          [hi, c],
          [hi, hi],
        ];
      default:
        return [[c, c]];
    }
  }

  private updateDieFace(
    container: Phaser.GameObjects.Container,
    value: number,
    size: number,
  ): void {
    this.drawPips(container, value, size);
  }

  /* =================================================================
   *  DICE ROLL ANIMATION
   * ================================================================= */

  private animateDiceRoll(
    die1Value: number,
    die2Value: number,
  ): Promise<void> {
    return new Promise((resolve) => {
      const W = this.scene.scale.width;
      const H = this.scene.scale.height;
      const dieSize = Math.max(W * 0.04, 30);

      // Dice area: bottom-left of controls bar
      const diceAreaX = W * 0.06;
      const diceAreaY = H * 0.91;

      // Destroy previous dice and total text
      for (const dc of this.diceContainers) {
        try {
          dc.destroy();
        } catch {
          /* already destroyed */
        }
      }
      this.diceContainers = [];
      if (this.totalText) {
        this.totalText.destroy();
        this.totalText = null;
      }

      // Start position (center of table area)
      const startX = W * 0.5;
      const startY = H * 0.7;

      // End position (dice area in controls bar)
      const endX1 = diceAreaX;
      const endX2 = diceAreaX + dieSize * 1.6;
      const endY = diceAreaY;

      const die1 = this.createDie(startX - dieSize, startY, 1, dieSize);
      const die2 = this.createDie(startX + dieSize * 0.5, startY, 1, dieSize);

      this.diceContainers.push(die1, die2);

      // Start small and transparent
      die1.setScale(0.4).setAlpha(0.7);
      die2.setScale(0.4).setAlpha(0.7);

      // ======= PHASE 1 (0-600ms): Launch upward =======
      const midX1 = startX - dieSize * 1.5 + (Math.random() - 0.5) * W * 0.08;
      const midX2 = startX + dieSize * 1.5 + (Math.random() - 0.5) * W * 0.08;
      const midY = H * 0.4 + Math.random() * H * 0.05;

      const t1 = this.scene.tweens.add({
        targets: die1,
        x: midX1,
        y: midY,
        scaleX: 0.9,
        scaleY: 0.9,
        alpha: 1,
        duration: 600,
        ease: 'Quad.Out',
      });
      this.tweens.push(t1);

      const t2 = this.scene.tweens.add({
        targets: die2,
        x: midX2,
        y: midY + Math.random() * H * 0.03,
        scaleX: 0.9,
        scaleY: 0.9,
        alpha: 1,
        duration: 600,
        ease: 'Quad.Out',
      });
      this.tweens.push(t2);

      // Rotation during launch
      const rotSpeed1 = 300 + Math.random() * 400;
      const rotSpeed2 = 300 + Math.random() * 400;
      const t3 = this.scene.tweens.add({
        targets: die1,
        angle: rotSpeed1,
        duration: 600,
        ease: 'Linear',
      });
      this.tweens.push(t3);
      const t4 = this.scene.tweens.add({
        targets: die2,
        angle: -rotSpeed2,
        duration: 600,
        ease: 'Linear',
      });
      this.tweens.push(t4);

      // ======= PHASE 2 (600-1200ms): Tumble =======
      const tumbleTimer = this.scene.time.addEvent({
        delay: 80,
        callback: () => {
          this.updateDieFace(die1, Math.ceil(Math.random() * 6), dieSize);
          this.updateDieFace(die2, Math.ceil(Math.random() * 6), dieSize);
        },
        loop: true,
      });
      this.timers.push(tumbleTimer);

      const phase2Delay = this.scene.time.delayedCall(600, () => {
        const t5 = this.scene.tweens.add({
          targets: die1,
          angle: die1.angle + (Math.random() - 0.5) * 180,
          x: midX1 + (Math.random() - 0.5) * W * 0.04,
          y: midY + (Math.random() - 0.5) * H * 0.03,
          scaleX: 1,
          scaleY: 1,
          duration: 600,
          ease: 'Sine.InOut',
        });
        this.tweens.push(t5);

        const t6 = this.scene.tweens.add({
          targets: die2,
          angle: die2.angle + (Math.random() - 0.5) * 180,
          x: midX2 + (Math.random() - 0.5) * W * 0.04,
          y: midY + (Math.random() - 0.5) * H * 0.03,
          scaleX: 1,
          scaleY: 1,
          duration: 600,
          ease: 'Sine.InOut',
        });
        this.tweens.push(t6);
      });
      this.timers.push(phase2Delay as any);

      // ======= PHASE 3 (1200-1800ms): Bounce to settle position =======
      const phase3Delay = this.scene.time.delayedCall(1200, () => {
        const settleX1 = endX1 + (Math.random() - 0.5) * dieSize * 0.3;
        const settleX2 = endX2 + (Math.random() - 0.5) * dieSize * 0.3;
        const settleY = endY + (Math.random() - 0.5) * dieSize * 0.2;

        const t7 = this.scene.tweens.add({
          targets: die1,
          x: settleX1,
          y: settleY,
          angle: (Math.random() - 0.5) * 15,
          duration: 600,
          ease: 'Bounce.Out',
        });
        this.tweens.push(t7);

        const t8 = this.scene.tweens.add({
          targets: die2,
          x: settleX2,
          y: settleY + (Math.random() - 0.5) * dieSize * 0.15,
          angle: (Math.random() - 0.5) * 15,
          duration: 600,
          ease: 'Bounce.Out',
        });
        this.tweens.push(t8);

        // Slow down face changes
        tumbleTimer.destroy();
        this.timers = this.timers.filter((t) => t !== tumbleTimer);

        const slowTimer = this.scene.time.addEvent({
          delay: 150,
          callback: () => {
            this.updateDieFace(die1, Math.ceil(Math.random() * 6), dieSize);
            this.updateDieFace(die2, Math.ceil(Math.random() * 6), dieSize);
          },
          loop: true,
        });
        this.timers.push(slowTimer);

        // ======= PHASE 4 (1800-2200ms): Reveal final =======
        const phase4Delay = this.scene.time.delayedCall(600, () => {
          slowTimer.destroy();
          this.timers = this.timers.filter((t) => t !== slowTimer);

          this.updateDieFace(die1, Math.ceil(Math.random() * 6), dieSize);
          this.updateDieFace(die2, Math.ceil(Math.random() * 6), dieSize);

          const finalDelay = this.scene.time.delayedCall(120, () => {
            // Set final values
            this.updateDieFace(die1, die1Value, dieSize);
            this.updateDieFace(die2, die2Value, dieSize);

            // Straighten dice
            const t9 = this.scene.tweens.add({
              targets: die1,
              angle: 0,
              duration: 200,
              ease: 'Sine.Out',
            });
            this.tweens.push(t9);
            const t10 = this.scene.tweens.add({
              targets: die2,
              angle: 0,
              duration: 200,
              ease: 'Sine.Out',
            });
            this.tweens.push(t10);

            // Scale pulse
            const t11 = this.scene.tweens.add({
              targets: die1,
              scaleX: 1.15,
              scaleY: 1.15,
              duration: 150,
              yoyo: true,
              ease: 'Sine.InOut',
            });
            this.tweens.push(t11);
            const t12 = this.scene.tweens.add({
              targets: die2,
              scaleX: 1.15,
              scaleY: 1.15,
              duration: 150,
              yoyo: true,
              ease: 'Sine.InOut',
            });
            this.tweens.push(t12);

            // Golden glow
            this.addDieGlow(die1, dieSize);
            this.addDieGlow(die2, dieSize);

            // Show total
            const totalVal = die1Value + die2Value;
            const totalMidX = (die1.x + die2.x) / 2 + dieSize / 2;
            const totalY = Math.min(die1.y, die2.y) - dieSize * 0.5;

            if (this.totalText) {
              this.totalText.destroy();
            }
            this.totalText = this.scene.add
              .text(totalMidX, totalY, `${totalVal}`, {
                fontSize: Math.max(Math.round(dieSize * 0.6), 18) + 'px',
                fontFamily: '"Arial Black", Arial, sans-serif',
                color: '#ffffff',
                fontStyle: 'bold',
                stroke: '#000000',
                strokeThickness: Math.max(3, Math.round(dieSize * 0.08)),
              })
              .setOrigin(0.5)
              .setDepth(112)
              .setAlpha(0);
            this.elements.push(this.totalText);

            const t13 = this.scene.tweens.add({
              targets: this.totalText,
              alpha: 1,
              duration: 300,
              ease: 'Sine.Out',
            });
            this.tweens.push(t13);

            // Resolve after settle
            const finishDelay = this.scene.time.delayedCall(400, () => {
              resolve();
            });
            this.timers.push(finishDelay as any);
          });
          this.timers.push(finalDelay as any);
        });
        this.timers.push(phase4Delay as any);
      });
      this.timers.push(phase3Delay as any);
    });
  }

  private addDieGlow(container: Phaser.GameObjects.Container, size: number): void {
    const glow = this.scene.add.graphics();
    const pad = size * 0.06;
    glow.lineStyle(Math.max(3, size * 0.06), 0xe6b800, 1);
    glow.strokeRoundedRect(-pad, -pad, size + pad * 2, size + pad * 2, size * 0.2);
    glow.setAlpha(0);
    container.add(glow);

    const t = this.scene.tweens.add({
      targets: glow,
      alpha: 1,
      duration: 250,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.InOut',
    });
    this.tweens.push(t);
  }

  /* =================================================================
   *  RESULT DISPLAY — Flash zones, show payouts
   * ================================================================= */

  private showBetResults(results: CrapsBetResult[]): void {
    for (const res of results) {
      let zoneKey = res.bet.type;
      if (res.bet.number) {
        zoneKey = `PLACE_${res.bet.number}`;
      }
      const zone = this.betZones.find((z) => z.key === zoneKey);
      if (!zone) continue;

      const { x, y, w, h } = zone.rect;
      const cornerR = Math.min(w, h) * 0.08;
      const flash = this.scene.add.graphics();
      flash.setDepth(108);

      if (res.outcome === 'WIN') {
        // Green flash
        flash.fillStyle(0x00ff00, 0.35);
        flash.fillRoundedRect(x, y, w, h, cornerR);

        // Floating payout text
        const W = this.scene.scale.width;
        const payTxt = this.scene.add
          .text(x + w / 2, y + h / 2, `+$${res.payout}`, {
            fontSize: Math.max(Math.round(W * 0.016), 12) + 'px',
            fontFamily: '"Arial Black", Arial, sans-serif',
            color: '#00ff00',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 3,
          })
          .setOrigin(0.5)
          .setDepth(115);
        this.elements.push(payTxt);

        const tw = this.scene.tweens.add({
          targets: payTxt,
          y: y + h / 2 - h * 0.6,
          alpha: 0,
          duration: 1500,
          ease: 'Sine.Out',
        });
        this.tweens.push(tw);
      } else if (res.outcome === 'LOSE') {
        // Red flash
        flash.fillStyle(0xff0000, 0.35);
        flash.fillRoundedRect(x, y, w, h, cornerR);
      } else {
        // Yellow flash for push
        flash.fillStyle(0xffff00, 0.2);
        flash.fillRoundedRect(x, y, w, h, cornerR);
      }

      this.elements.push(flash);

      const tw = this.scene.tweens.add({
        targets: flash,
        alpha: 0,
        duration: 1200,
        delay: 400,
        onComplete: () => flash.destroy(),
      });
      this.tweens.push(tw);
    }
  }

  private showWin(payout: number): void {
    if (!this.resultText) return;
    this.resultText.setText(`+$${payout}!`);
    this.resultText.setColor('#e6b800');
    this.resultText.setAlpha(0);

    this.resultText.setScale(0.3);
    const t1 = this.scene.tweens.add({
      targets: this.resultText,
      alpha: 1,
      scaleX: 1.3,
      scaleY: 1.3,
      duration: 350,
      ease: 'Back.Out',
      onComplete: () => {
        const t2 = this.scene.tweens.add({
          targets: this.resultText,
          scaleX: 1,
          scaleY: 1,
          duration: 200,
          ease: 'Sine.Out',
        });
        this.tweens.push(t2);
      },
    });
    this.tweens.push(t1);
  }

  private showLoss(): void {
    if (!this.resultText) return;
    this.resultText.setText('Perdu!');
    this.resultText.setColor('#ff4444');
    this.resultText.setAlpha(0);

    const t = this.scene.tweens.add({
      targets: this.resultText,
      alpha: 1,
      duration: 300,
      ease: 'Sine.Out',
    });
    this.tweens.push(t);
  }

  private showError(msg: string): void {
    if (!this.resultText) return;
    this.resultText.setText(msg);
    this.resultText.setColor('#ff4444');
    this.resultText.setAlpha(1);

    const timer = this.scene.time.delayedCall(2000, () => {
      if (this.resultText) this.resultText.setAlpha(0);
    });
    this.timers.push(timer as any);
  }

  /* =================================================================
   *  HELPERS
   * ================================================================= */

  private getToggleLabel(): string {
    return this.useDirty ? 'Sale' : 'Propre';
  }

  private getBalanceLabel(): string {
    return `BALANCE  Propre: $${Math.floor(this.balanceClean)}  |  Sale: $${Math.floor(this.balanceDirty)}`;
  }

  private updateCurrentBetText(): void {
    if (!this.currentBetText) return;
    const total = this.pendingBets.reduce((s, b) => s + b.amount, 0);
    this.currentBetText.setText(`$${total.toFixed(2)} CURRENT BET`);
  }

  /* =================================================================
   *  CLEANUP — destroy ALL elements, timers, tweens
   * ================================================================= */

  destroy(): void {
    // Destroy all timers
    for (const t of this.timers) {
      try {
        t.destroy();
      } catch {
        /* already destroyed */
      }
    }
    this.timers = [];

    // Stop all tweens
    for (const t of this.tweens) {
      try {
        t.stop();
      } catch {
        /* already stopped */
      }
    }
    this.tweens = [];

    // Destroy chip containers
    for (const c of this.chipContainers) {
      try {
        c.destroy();
      } catch {
        /* ok */
      }
    }
    this.chipContainers = [];

    // Destroy chip selectors
    for (const c of this.chipSelectors) {
      try {
        c.destroy();
      } catch {
        /* ok */
      }
    }
    this.chipSelectors = [];

    // Destroy dice containers
    for (const dc of this.diceContainers) {
      try {
        dc.destroy();
      } catch {
        /* ok */
      }
    }
    this.diceContainers = [];

    // Destroy all elements
    for (const e of this.elements) {
      try {
        e.destroy();
      } catch {
        /* ok */
      }
    }
    this.elements = [];

    // Nullify refs
    this.balanceText = null;
    this.currentBetText = null;
    this.phaseText = null;
    this.resultText = null;
    this.rollBtn = null;
    this.toggleText = null;
    this.puckGraphics = null;
    this.puckText = null;
    this.totalText = null;
    this.zoneGraphics = null;
  }
}
